/**
 * useFunChallengesUserSearch
 *
 * Wraps the existing CircleChess `online_players_search` flow so the
 * FunChallenges lobby can reuse `ChallengeUserComponent` (the chess search
 * dropdown) without leaning on PlayChess having mounted first.
 *
 * - Reads `gameClient` from `SocketContext`.
 * - Idempotently registers the outgoing `online_players_search` schema and
 *   an inbound `online_players_result` handler keyed under
 *   `'FunChallenges:online_players_result'`.
 * - Exposes `{ searchPlayers(pattern), searchPlayersResult }` so it drops
 *   straight into `<ChallengeUserComponent>`.
 *
 * Notes
 *   The chess flow registers the same handler under
 *   `'PlayChess:online_players_search'` — handler keys are distinct so this
 *   coexists peacefully and both update their own local state.
 */

import {useContext, useEffect, useState} from 'react';

import {SocketContext} from '@/Providers/SocketProvider';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import {hardenDecode} from '@/Hooks/FunChallengesSocket/hardenDecode';

const {schema} = require('../../../CCClientSdk');

const RESULT_HANDLER_KEY = 'FunChallenges:online_players_result';

/** Module-level guard so we only register once per socket identity. */
let registeredSocket: any = null;

export interface FunChallengesUserSearchApi {
  searchPlayers: (pattern: string) => void;
  /** JSON-encoded string from the server — `ChallengeUserComponent` parses it itself. */
  searchPlayersResult: string;
  username: string;
}

export function useFunChallengesUserSearch(): FunChallengesUserSearchApi {
  const ctx = useContext(SocketContext);
  const socket = ctx?.socket ?? null;

  // `loggedInUserstore.userId` is never populated app-wide; the authoritative
  // value is `userData.user_id` set during login. Fall back to `userId` only
  // if that ever starts being populated. This is the value
  // `ChallengeUserComponent` uses to filter the dropdown so the current user
  // doesn't see themselves in the opponent picker.
  const usernameFromStore = useLoggedInUserStore(s => s.userId);
  const usernameFromUserData = useLoggedInUserStore(
    s => (s.userData && (s.userData.user_id ?? s.userData.pk)) || '',
  );
  const username = String(usernameFromUserData || usernameFromStore || '');

  const [searchPlayersResult, setSearchPlayersResult] = useState<string>('');

  useEffect(() => {
    if (!socket) return;

    const handler = (_socket: any, data: any) => {
      if (data && typeof data.players === 'string') {
        setSearchPlayersResult(data.players);
      }
    };

    if (registeredSocket !== socket) {
      try {
        if (schema?.onlinePlayersSearchMsgSchema) {
          // Outgoing schema (no-op handler — sender doesn't need to listen).
          socket.registerMessageType(
            'online_players_search',
            schema.onlinePlayersSearchMsgSchema,
            () => {},
          );
          hardenDecode(socket, 'online_players_search');
        }
        if (schema?.onlinePlayersResultMsgSchema) {
          socket.registerMessageType(
            'online_players_result',
            schema.onlinePlayersResultMsgSchema,
            handler,
          );
          // socket.io delivers binary frames to the browser as
          // {type:'Buffer',data:[…]} which the stock decoder crashes on;
          // harden this inbound decoder (see hardenDecode for the full why).
          hardenDecode(socket, 'online_players_result');
        }
      } catch (err) {
        console.warn('[FunChallenges] user-search registration failed', err);
      }
      registeredSocket = socket;
    }

    // Always (re)attach a handler under our own key so this hook unmounting
    // and remounting doesn't lose the response.
    try {
      socket.registerMessageHandler?.(
        'online_players_result',
        handler,
        RESULT_HANDLER_KEY,
      );
    } catch (err) {
      console.warn('[FunChallenges] result handler attach failed', err);
    }
  }, [socket]);

  const searchPlayers = (pattern: string) => {
    if (!socket || typeof socket.send_msg !== 'function') return;
    setSearchPlayersResult('');
    try {
      socket.send_msg('online_players_search', {
        requestId: String(Date.now()),
        userId: String(username || ''),
        type: 'online_players_search',
        pattern: pattern || '',
      });
    } catch (err) {
      console.warn('[FunChallenges] online_players_search send failed', err);
    }
  };

  return {
    searchPlayers,
    searchPlayersResult,
    username: String(username || ''),
  };
}

export default useFunChallengesUserSearch;
